<template>
  <AIVDialog
    v-model="isVisible"
    :title="modalTitle"
    width="500px"
    height="85vh"
    :closable="!isForced"
    class="change-password-modal"
  >
    <div v-if="isForced" class="warning-message">
      <p>You are currently using the default password. For security reasons, you must change your password before continuing.</p>
    </div>

    <div v-if="isAdmin" class="info-message">
      <p>Changing the password for <strong>{{ caregiverName }}</strong></p>
    </div>

    <form @submit.prevent="handleSubmit">
      <div v-if="!isAdmin" class="form-group">
        <label>Current Password</label>
        <div class="password-input-wrapper">
          <el-input
            v-model="currentPassword"
            :type="showCurrentPassword ? 'text' : 'password'"
            autocomplete="current-password"
          />
          <i
            :class="showCurrentPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
            @click="showCurrentPassword = !showCurrentPassword"
            class="password-toggle-icon"
            aria-hidden="true"
          />
        </div>
      </div>

      <div class="form-group">
        <label>New Password</label>
        <div class="password-input-wrapper">
          <el-input
            v-model="newPassword"
            :type="showNewPassword ? 'text' : 'password'"
            autocomplete="new-password"
          />
          <i
            :class="showNewPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
            @click="showNewPassword = !showNewPassword"
            class="password-toggle-icon"
            aria-hidden="true"
          />
        </div>
      </div>

      <div class="form-group">
        <label>Confirm New Password</label>
        <div class="password-input-wrapper">
          <el-input
            v-model="confirmPassword"
            :type="showConfirmPassword ? 'text' : 'password'"
            autocomplete="new-password"
          />
          <i
            :class="showConfirmPassword ? 'fa-solid fa-eye-slash' : 'fa-solid fa-eye'"
            @click="showConfirmPassword = !showConfirmPassword"
            class="password-toggle-icon"
            aria-hidden="true"
          />
        </div>
      </div>

      <div class="password-requirements">
        <small class="form-text">Password must contain:</small>
        <ul class="requirements-list">
          <li :class="{ valid: hasMinLength }">At least 8 characters</li>
          <li :class="{ valid: hasNumber }">At least one number</li>
          <li :class="{ valid: hasSpecialChar }">At least one special character</li>
        </ul>
      </div>
    </form>

    <template slot="footer">
      <div class="text-center">
        <button
          type="button"
          class="btn btn-fill btn-default"
          @click="handleCancel"
          :disabled="loading"
        >
          Cancel
        </button>
        <button
          type="submit"
          class="btn btn-fill btn-info"
          @click="handleSubmit"
          :disabled="loading"
        >
          {{ loading ? 'Changing...' : 'Change Password' }}
        </button>
      </div>
    </template>
  </AIVDialog>
</template>

<script>
import { useLoginStore } from 'src/stores/loginStore';
import commonHandler from 'src/serviceHandlers/commonHandler';

const apiService = new commonHandler();

export default {
  name: 'ChangePasswordModal',
  props: {
    value: {
      type: Boolean,
      default: false
    },
    isForced: {
      type: Boolean,
      default: false
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    caregiverId: {
      type: Number,
      default: null
    },
    caregiverName: {
      type: String,
      default: ''
    },
    subjectLabel: {
      type: String,
      default: 'Caregiver'
    }
  },
  data() {
    return {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      showCurrentPassword: false,
      showNewPassword: false,
      showConfirmPassword: false,
      loading: false
    };
  },
  computed: {
    isVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    modalTitle() {
      if (this.isAdmin) {
        return `Change ${this.subjectLabel} Password`;
      }
      return this.isForced ? 'Password Change Required' : 'Change Password';
    },
    hasMinLength() {
      return this.newPassword.length >= 8;
    },
    hasNumber() {
      return /\d/.test(this.newPassword);
    },
    hasSpecialChar() {
      return /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(this.newPassword);
    }
  },
  watch: {
    value(newValue) {
      if (newValue) {
        this.resetForm();
      }
    }
  },
  methods: {
    resetForm() {
      this.currentPassword = '';
      this.newPassword = '';
      this.confirmPassword = '';
      this.showCurrentPassword = false;
      this.showNewPassword = false;
      this.showConfirmPassword = false;
      this.loading = false;
    },

    handleClose() {
      if (!this.isForced && !this.loading) {
        this.isVisible = false;
      }
    },

    handleCancel() {
      if (this.loading) return;

      if (this.isForced) {
        // User is refusing to change password from default, log them out
        localStorage.removeItem('jwtToken');
        const loginStore = useLoginStore();
        loginStore.$reset();

        // Destroy auth service timers
        try {
          const authService = require('@/services/authService').default;
          authService.destroy();
        } catch (e) {
          // AuthService might not be available in all contexts
        }

        // Only navigate if we're not already on login page
        if (this.$route.name !== 'Login') {
          this.$router.push({
            name: 'Login',
            query: { message: 'You must change your password from the default to continue.' }
          });
        } else {
          // Already on login page, just close modal and show message
          this.isVisible = false;
        }
      } else {
        // Voluntary password change, just close the modal
        this.isVisible = false;
      }
    },

    validatePassword(password) {
      // At least 8 characters
      if (password.length < 8) {
        return { valid: false, message: 'Password must be at least 8 characters long.' };
      }
      // At least one number
      if (!/\d/.test(password)) {
        return { valid: false, message: 'Password must contain at least one number.' };
      }
      // At least one special character
      if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
        return { valid: false, message: 'Password must contain at least one special character.' };
      }
      return { valid: true };
    },

    async handleSubmit() {
      // Validation
      const passwordValidation = this.validatePassword(this.newPassword);
      if (!passwordValidation.valid) {
        this.$notifyError(passwordValidation.message);
        return;
      }

      if (this.newPassword !== this.confirmPassword) {
        this.$notifyError('New password and confirmation do not match');
        return;
      }

      // Only check current password match for non-admin mode
      if (!this.isAdmin && this.newPassword === this.currentPassword) {
        this.$notifyError('New password must be different from current password');
        return;
      }

      if (this.newPassword === 'Test1212!') {
        this.$notifyError('Cannot use the default password. Please choose a different password.');
        return;
      }

      // Admin mode validation
      if (this.isAdmin && !this.caregiverId) {
        this.$notifyError('Invalid user ID');
        return;
      }

      this.loading = true;

      try {
        let response;

        if (this.isAdmin) {
          // Admin changing someone else's password
          response = await apiService.adminChangePassword({
            caregiverId: this.caregiverId,
            newPassword: this.newPassword
          });
        } else {
          // User changing their own password
          response = await apiService.changePassword({
            currentPassword: this.currentPassword,
            newPassword: this.newPassword
          });
        }

        if (response.data.success) {
          this.$emit('success');
          this.resetForm();
        } else {
          this.$notifyError(response.data.message || 'Failed to change password');
          this.loading = false;
        }
      } catch (error) {
        this.$notifyError(error.response?.data?.message || 'An error occurred while changing the password');
        this.loading = false;
      }
    }
  }
};
</script>

<style scoped>
.warning-message {
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 20px;
}

.warning-message p {
  margin: 0;
  color: #fff;
}

.info-message {
  margin-bottom: 20px;
}

.info-message p {
  margin: 0;
  color: #fff;
}

.info-message strong {
  color: #fff;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  color: #d9d9d9;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 0.5rem;
  display: block;
}

.password-input-wrapper {
  position: relative;
}

.password-toggle-icon {
  position: absolute;
  top: 50%;
  right: 12px;
  transform: translateY(-50%);
  cursor: pointer;
  color: #ffffff !important;
  z-index: 10;
  font-size: 16px;
  pointer-events: auto;
}

.password-toggle-icon:hover {
  color: #ffffff !important;
}

.form-text {
  display: block;
  margin-top: 5px;
  color: #b3a6a6;
  font-size: 0.875rem;
}

.password-requirements {
  margin-top: 8px;
}

.requirements-list {
  list-style: none;
  padding: 0;
  margin: 5px 0 0 0;
}

.requirements-list li {
  font-size: 0.875rem;
  color: #b3a6a6;
  padding: 2px 0;
  transition: color 0.2s;
}

.requirements-list li::before {
  content: '✗ ';
  color: #dc3545;
  margin-right: 5px;
  font-weight: bold;
}

.requirements-list li.valid {
  color: #28a745;
}

.requirements-list li.valid::before {
  content: '✓ ';
  color: #28a745;
}

/* Element UI input styling to match other modals */
:deep(.el-input__inner) {
  background-color: #656565 !important;
  color: #ffffff !important;
  border-radius: 6px !important;
  height: 40px !important;
  font-size: 14px !important;
  font-weight: normal !important;
}

:deep(.el-input__inner:focus) {
  border-color: #ffffff !important;
  box-shadow: none !important;
  background-color: #656565 !important;
  color: #ffffff !important;
}

.text-center button {
  margin: 0 8px;
}

/* Make modal body scrollable and keep footer visible on small screens */
:deep(.change-password-modal .el-dialog__body) {
  overflow-y: auto;
  max-height: calc(85vh - 120px);
}
</style>
